import React, { useEffect, useRef, useState } from 'react';
import { FaTable } from '@react-icons/all-files/fa/FaTable';
import { FaUmbrellaBeach } from '@react-icons/all-files/fa/FaUmbrellaBeach';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../../../../../commons/Breadcrumb';
import PageHeader from '../../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../../../commons/Confirm';
import { DeleteButton, RenderActions } from '../../../../../commons/DataGridActions';
import ProfaseDataGrid from '../../../../../commons/Datagrid';
import DateFormat from '../../../../../commons/componentes/DateFormat';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import { deleteEntity, getEntities, reset } from '../../../../../manager/calendario-dia-festivo-instancia.reducer';

const FERIADOS = '/configuracion/calendario/feriados';

const MESES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

function fmtTipo(tipo?: string | null) {
  if (tipo === 'semana') return 'Por semana';
  if (tipo === 'fecha') return 'Fecha fija';
  return tipo ?? '—';
}

function fmtReglaResumen(regla: any) {
  if (!regla) return '—';
  const mes = regla.mes != null ? MESES[regla.mes - 1] ?? regla.mes : '';
  if (regla.tipo === 'semana') {
    return `${mes} · ${regla.semana ?? '?'}ª sem · día ${regla.dia ?? '?'}`;
  }
  if (regla.tipo === 'fecha') {
    return `${mes} ${regla.dia ?? '?'}`;
  }
  return regla.nombre ?? '—';
}

function instanciaLabel(row: any) {
  const df = row.diaFestivo;
  const regla = row.calendarioDiaFestivo;
  const fecha = df?.fecha ? String(df.fecha).slice(0, 10) : '';
  const desc = df?.descripcion ?? '';
  const festivo = desc && fecha ? `${desc} (${fecha})` : desc || fecha || `#${row.id}`;
  const reglaNombre = regla?.nombre;
  return reglaNombre ? `${festivo} — regla: ${reglaNombre}` : festivo;
}

export default function TablaDiasFestivosPage() {
  const dispatch = useAppDispatch();
  const topRef = useRef<HTMLDivElement>(null);

  const entities = useAppSelector(s => s.calendarioDiaFestivoInstancia.entities);
  const totalItems = useAppSelector(s => s.calendarioDiaFestivoInstancia.totalItems);
  const loading = useAppSelector(s => s.calendarioDiaFestivoInstancia.loading);
  const updating = useAppSelector(s => s.calendarioDiaFestivoInstancia.updating);
  const updateSuccess = useAppSelector(s => s.calendarioDiaFestivoInstancia.updateSuccess);

  const [rows, setRows] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    if (updateSuccess) {
      dispatch(reset());
      dismiss();
      if (lastParams) dispatch(getEntities(lastParams));
      showToast('success', 'Vínculo de día festivo eliminado correctamente.');
    }
  }, [updateSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(updating); }, [updating]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(entities ?? []); }, [entities]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <DeleteButton onClick={() => {
        ask(
          <>
            ¿Confirmas que deseas eliminar el vínculo <strong>{instanciaLabel(params.row)}</strong>?
            {' '}No se eliminará la regla de calendario ni el día festivo asociado.
          </>,
          () => dispatch(deleteEntity(params.row.id)),
          { title: 'Eliminar vínculo', confirmLabel: 'Eliminar', variant: 'danger' },
        );
        topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id', headerName: 'ID', flex: 0.45 },
    {
      field: 'diaFestivo.fecha',
      headerName: 'Fecha',
      flex: 1,
      renderCell: (p: any) => {
        const fecha = p.row.diaFestivo?.fecha;
        return fecha
          ? <DateFormat value={fecha} />
          : <span style={{ color: '#94a3b8' }}>—</span>;
      },
    },
    {
      field: 'diaFestivo.descripcion',
      headerName: 'Descripción',
      flex: 2,
      renderCell: (p: any) => p.row.diaFestivo?.descripcion ?? '—',
    },
    {
      field: 'calendarioDiaFestivo.nombre',
      headerName: 'Regla calendario',
      flex: 2,
      renderCell: (p: any) => p.row.calendarioDiaFestivo?.nombre ?? '—',
    },
    {
      field: 'calendarioDiaFestivo.tipo',
      headerName: 'Tipo regla',
      flex: 1,
      renderCell: (p: any) => fmtTipo(p.row.calendarioDiaFestivo?.tipo),
    },
    {
      field: 'calendarioDiaFestivo.mes',
      headerName: 'Regla',
      flex: 1.1,
      renderCell: (p: any) => fmtReglaResumen(p.row.calendarioDiaFestivo),
    },
    { field: '__actions', headerName: '', flex: 0.55, renderCell: renderActions },
  ];

  return (
    <div style={g.page} ref={topRef}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Calendario', to: '/configuracion/calendario', icon: <FaCalendarAlt /> },
        { label: 'Feriados', to: FERIADOS, icon: <FaUmbrellaBeach /> },
        { label: 'Tabla de Días Festivos', icon: <FaTable /> },
      ]} />

      <PageHeader
        icon={<FaTable />}
        iconColor="#8b5cf6"
        title="Tabla de Días Festivos"
        subtitle="Instancias que vinculan reglas de calendario con días festivos materializados"
      />

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={[]}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
        defaultSortField="id"
      />
    </div>
  );
}

const g: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
};
